import FormPublicWrapper from "@/components/form-public-wrapper";
import { InputIcon } from "@/components/input-icon";
import {
  PublicAuthCard,
  PublicAuthSubmitButton,
  publicAuthInputClassName,
  publicAuthLabelClassName,
} from "@/components/public-auth-card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import Routes from "@/routes/paths";
import { UserService, parseUser } from "@/services/api/user";
import StorageToken from "@/services/storage/token";
import StorageUser from "@/services/storage/user";
import { cn } from "@/utils";
import { getToastErrorContent } from "@/utils/functions/api-error";
import { useUserProvider } from "@/utils/providers/user-provider";
import { zodResolver } from "@hookform/resolvers/zod";
import { EyeIcon, EyeOffIcon, Lock, Mail } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import * as z from "zod";

const FormSchema = z.object({
  email: z
    .string()
    .min(1, { message: "Campo obrigatório" })
    .email({ message: "E-mail inválido" }),
  password: z.string().min(1, { message: "Campo obrigatório" }),
});

type FormSchemaType = z.infer<typeof FormSchema>;

export default function SignInScreen() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { setUser } = useUserProvider();
  const [passwordType, setPasswordType] = useState("password");

  const form = useForm<FormSchemaType>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  async function onSubmit(data: FormSchemaType) {
    try {
      const { token, user } = await UserService.login(FormSchema.parse(data));

      const parsedUser = parseUser(user);

      setUser(parsedUser);

      StorageUser.set(parsedUser);
      StorageToken.set(token);

      navigate(Routes.home, { replace: true });
    } catch (error) {
      toast(getToastErrorContent(error));
    }
  }

  const togglePasswordInputType = () =>
    setPasswordType((prev) => (prev === "password" ? "text" : "password"));

  const { isSubmitting } = form.formState;

  const InputPasswordIcon = passwordType === "password" ? EyeOffIcon : EyeIcon;

  return (
    <FormPublicWrapper variant="modern">
      <PublicAuthCard
        title="Bem-vindo de volta"
        description="Entre com suas credenciais para acessar o painel"
      >
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className={publicAuthLabelClassName}>
                    E-mail
                  </FormLabel>

                  <FormControl>
                    <div className="relative">
                      <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                      <Input
                        placeholder="seu@email.com"
                        autoComplete="email"
                        className={publicAuthInputClassName}
                        {...field}
                      />
                    </div>
                  </FormControl>

                  <FormMessage className="text-red-400" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center justify-between">
                    <FormLabel className={publicAuthLabelClassName}>
                      Senha
                    </FormLabel>

                    <Link
                      to={Routes.account.forgotPassword}
                      className="text-xs font-medium text-emerald-400 transition-colors hover:text-emerald-300"
                    >
                      Esqueceu a senha?
                    </Link>
                  </div>

                  <FormControl>
                    <div className="relative">
                      <Lock className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-slate-500" />
                      <InputIcon
                        icon={
                          <InputPasswordIcon
                            size={18}
                            className="text-slate-400 transition-colors hover:text-slate-200"
                          />
                        }
                        onClick={togglePasswordInputType}
                      >
                        <Input
                          {...field}
                          type={passwordType}
                          placeholder="••••••••"
                          autoComplete="current-password"
                          className={cn(publicAuthInputClassName, "pr-10")}
                        />
                      </InputIcon>
                    </div>
                  </FormControl>

                  <FormMessage className="text-red-400" />
                </FormItem>
              )}
            />

            <PublicAuthSubmitButton
              isSubmitting={isSubmitting}
              loadingText="Entrando..."
            >
              Entrar
            </PublicAuthSubmitButton>
          </form>
        </Form>
      </PublicAuthCard>
    </FormPublicWrapper>
  );
}
